// client/src/components/StatisticsDashboard.js

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Divider,
  Alert,
  LinearProgress,
  Chip
} from '@mui/material';
import axiosInstance from '../axiosInstance';
import { 
  BarChart, 
  Bar, 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

// Colors for charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const StatisticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [timeRange, setTimeRange] = useState(24);
  const [catalogueStats, setCatalogueStats] = useState(null);

  useEffect(() => {
    fetchStatistics();
  }, [timeRange]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const authHeader = { headers: { Authorization: `Bearer ${token}` } };
      const [statsRes, catalogueRes] = await Promise.allSettled([
        axiosInstance.get(`/admin/statistics?timeRange=${timeRange}`, authHeader),
        axiosInstance.get('/admin/catalogue-stats', authHeader),
      ]);
      if (statsRes.status === 'fulfilled') setStatistics(statsRes.value.data);
      if (catalogueRes.status === 'fulfilled') setCatalogueStats(catalogueRes.value.data);
      if (statsRes.status === 'rejected') throw statsRes.reason;
      setError(null);
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError('Failed to load statistics. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ m: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!statistics) {
    return (
      <Box sx={{ m: 2 }}>
        <Alert severity="info">No statistics available.</Alert>
      </Box>
    );
  }

  // Format timestamp for display
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Format date for display
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString();
  };

  // Prepare data for status code distribution pie chart with direct count values
  const statusCodeData = [];
  const statusCodes = statistics.performance.statusCodeDistribution;
  
  // Calculate total requests for percentage calculations
  const totalRequests = statusCodes.reduce((sum, item) => sum + item.count, 0);
  
  // Define status code color mapping
  const getStatusCodeColor = (code) => {
    const codeNum = parseInt(code);
    if (codeNum >= 200 && codeNum < 300) return '#0088FE'; // Blue for 2xx
    if (codeNum >= 300 && codeNum < 400) return '#FFBB28'; // Yellow for 3xx
    if (codeNum >= 400 && codeNum < 500) return '#00C49F'; // Green for 4xx
    if (codeNum >= 500 && codeNum < 600) return '#FF8042'; // Orange for 5xx
    return '#8884d8'; // Default purple
  };
  
  // Only proceed if we have data
  if (totalRequests > 0) {
    // Map the data with the count and color
    statusCodes.forEach(item => {
      const percentage = (item.count / totalRequests) * 100;
      statusCodeData.push({
        name: `${item.statusCode}`,
        count: item.count,
        percentage: percentage.toFixed(1),
        color: getStatusCodeColor(item.statusCode)
      });
    });
  }

  // Prepare data for cache performance pie chart with better handling of edge cases
  let cacheData = [];
  const hits = statistics.performance.cachePerformance.hits || 0;
  const misses = statistics.performance.cachePerformance.misses || 0;
  const total = hits + misses;
  
  // Calculate hit ratio percentage with proper handling of edge cases
  let hitRatio = 0;
  if (total > 0) {
    hitRatio = (hits / total) * 100;
  }
  
  // Handle the case where both hits and misses are 0
  if (total === 0) {
    // Add a placeholder to show an empty chart
    cacheData = [
      { name: 'No Cache Data', value: 1, color: '#9e9e9e' }
    ];
  } else {
    cacheData = [
      { name: 'Cache Hits', value: hits, color: '#4caf50' },
      { name: 'Cache Misses', value: misses, color: '#f44336' }
    ];
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Application Statistics</Typography>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="time-range-label">Time Range</InputLabel>
          <Select
            labelId="time-range-label"
            value={timeRange}
            label="Time Range"
            onChange={handleTimeRangeChange}
          >
            <MenuItem value={6}>Last 6 Hours</MenuItem>
            <MenuItem value={12}>Last 12 Hours</MenuItem>
            <MenuItem value={24}>Last 24 Hours</MenuItem>
            <MenuItem value={48}>Last 2 Days</MenuItem>
            <MenuItem value={168}>Last Week</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Basic Statistics Cards */}
      <Typography variant="h5" sx={{ mb: 2 }}>System Overview</Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Users
              </Typography>
              <Typography variant="h3">
                {statistics.basic.users.total}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Active: {statistics.basic.users.active} | Disabled: {statistics.basic.users.disabled}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Games
              </Typography>
              <Typography variant="h3">
                {statistics.basic.collections.games}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                In Collections
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Consoles
              </Typography>
              <Typography variant="h3">
                {statistics.basic.collections.consoles}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                In Collections
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total LEGO Sets
              </Typography>
              <Typography variant="h3">
                {statistics.basic.collections.legoSets}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                In Collections
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        {statistics.basic.collections.tradingCards > 0 && (
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Trading Cards
                </Typography>
                <Typography variant="h3">
                  {statistics.basic.collections.tradingCards}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  In Collections
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
        {statistics.basic.collections.manga > 0 && (
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Manga
                </Typography>
                <Typography variant="h3">
                  {statistics.basic.collections.manga}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  In Collections
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
        {statistics.basic.collections.comics > 0 && (
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Comics
                </Typography>
                <Typography variant="h3">
                  {statistics.basic.collections.comics}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  In Collections
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Performance Statistics */}
      <Typography variant="h5" sx={{ mb: 2 }}>Performance Metrics</Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Response Times Table */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Top Endpoints by Request Count</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Endpoint</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell align="right">Requests</TableCell>
                    <TableCell align="right">Avg (ms)</TableCell>
                    <TableCell align="right">Min (ms)</TableCell>
                    <TableCell align="right">Max (ms)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statistics.performance.responseTimesByEndpoint.slice(0, 10).map((endpoint, index) => (
                    <TableRow key={index}>
                      <TableCell component="th" scope="row" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {endpoint.endpoint}
                      </TableCell>
                      <TableCell>{endpoint.method}</TableCell>
                      <TableCell align="right">{endpoint.requestCount}</TableCell>
                      <TableCell align="right">{endpoint.avgResponseTime}</TableCell>
                      <TableCell align="right">{endpoint.minResponseTime}</TableCell>
                      <TableCell align="right">{endpoint.maxResponseTime}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Status Code Distribution */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Status Code Distribution</Typography>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={statusCodeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ payload }) => {
                    // Use the pre-calculated percentage
                    return `${payload.percentage}%`;
                  }}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                  nameKey="name"
                >
                  {statusCodeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name, props) => {
                    const entry = statusCodeData.find(item => item.name === name);
                    return [`${value} requests (${entry?.percentage}%)`, `Status ${name}`];
                  }}
                  labelFormatter={() => 'Status Code Distribution'}
                />
              </PieChart>
            </ResponsiveContainer>
            
            {/* Status Code Definitions */}
            <Box sx={{ mt: 2, fontSize: '0.85rem' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>HTTP Status Code Definitions:</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {/* Display status code ranges with matching colors */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 16, height: 16, backgroundColor: '#0088FE', mr: 1, borderRadius: '50%' }} />
                  <Box><strong>2xx</strong> - Success: The request was successfully received and processed</Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 16, height: 16, backgroundColor: '#FFBB28', mr: 1, borderRadius: '50%' }} />
                  <Box><strong>3xx</strong> - Redirection: Further action needs to be taken to complete the request</Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 16, height: 16, backgroundColor: '#00C49F', mr: 1, borderRadius: '50%' }} />
                  <Box><strong>4xx</strong> - Client Error: The request contains bad syntax or cannot be fulfilled</Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 16, height: 16, backgroundColor: '#FF8042', mr: 1, borderRadius: '50%' }} />
                  <Box><strong>5xx</strong> - Server Error: The server failed to fulfill a valid request</Box>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Cache Performance */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Cache Performance</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ResponsiveContainer width="50%" height={200}>
                <PieChart>
                  <Pie
                    data={cacheData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => {
                      // Handle the case where percent might be NaN
                      const displayPercent = isNaN(percent) ? 0 : percent * 100;
                      return `${name}: ${displayPercent.toFixed(1)}%`;
                    }}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {cacheData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color || (index === 0 ? '#4caf50' : '#f44336')} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => {
                      if (name === 'No Cache Data') return ['No cache data available', 'Status'];
                      return [`${value} requests`, name];
                    }}
                    labelFormatter={() => 'Cache Performance'}
                  />
                </PieChart>
              </ResponsiveContainer>
              <Box sx={{ width: '50%', pl: 2 }}>
                <Typography variant="h4" sx={{ color: '#00C49F' }}>
                  {parseFloat(statistics.performance.cachePerformance.hitRatio || 0).toFixed(2)}%
                </Typography>
                <Typography variant="body1">Cache Hit Ratio</Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2">
                  Hits: {statistics.performance.cachePerformance.hits}
                </Typography>
                <Typography variant="body2">
                  Misses: {statistics.performance.cachePerformance.misses}
                </Typography>
                <Typography variant="body2">
                  Total: {statistics.performance.cachePerformance.total}
                </Typography>
              </Box>
            </Box>
            
            {/* Cache Performance Explanation */}
            <Box sx={{ mt: 2, fontSize: '0.85rem' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>About Cache Performance:</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box><strong>Cache Hits</strong> - Requests that were served from the cache, saving database queries</Box>
                <Box><strong>Cache Misses</strong> - Requests that couldn't be served from cache and required database access</Box>
                <Box><strong>Hit Ratio</strong> - Percentage of requests served from cache (higher is better)</Box>
                {statistics.performance.cachePerformance.hits === 0 && statistics.performance.cachePerformance.misses > 0 && (
                  <Box sx={{ mt: 1, color: 'warning.main', fontWeight: 'bold' }}>
                    Note: The cache currently shows 0% hit rate. This may indicate the cache system needs configuration or optimization.
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Hourly Activity */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Hourly Activity</Typography>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart
                data={statistics.userActivity.hourlyActivity}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="hour" 
                  tickFormatter={(value) => value}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(value) => `Time: ${value}`}
                  formatter={(value) => [`${value} requests`, 'Count']}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="requests" 
                  stroke="#8884d8" 
                  activeDot={{ r: 8 }} 
                  name="Requests"
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* User Activity */}
      <Typography variant="h5" sx={{ mb: 2 }}>User Activity</Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Most Active Users */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Most Active Users</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell align="right">Requests</TableCell>
                    <TableCell align="right">Avg Response Time (ms)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statistics.userActivity.mostActiveUsers.map((user, index) => (
                    <TableRow key={index}>
                      <TableCell component="th" scope="row">
                        {user.username}
                      </TableCell>
                      <TableCell align="right">{user.requestCount}</TableCell>
                      <TableCell align="right">{user.avgResponseTime}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* User Registrations */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>User Registrations by Month</Typography>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart
                data={statistics.growth.userRegistrations}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="period" 
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getFullYear().toString().substr(2, 2)}`;
                  }}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
                  }}
                  formatter={(value) => [`${value} users`, 'New Users']}
                />
                <Legend />
                <Bar dataKey="count" fill="#8884d8" name="New Users" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Collection Growth */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Collection Growth by Month</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={statistics.growth.collectionGrowth}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="period" 
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getFullYear().toString().substr(2, 2)}`;
                  }}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
                  }}
                  formatter={(value) => [`${value} items`, 'New Items']}
                />
                <Legend />
                <Bar dataKey="count" fill="#82ca9d" name="New Collection Items" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* API Harvester & Catalogue Stats */}
      {catalogueStats && (
        <>
          <Typography variant="h5" sx={{ mb: 2 }}>API Harvester &amp; Local Catalogue</Typography>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Catalogue counts */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Harvested Catalogue Counts</Typography>
                <Grid container spacing={2}>
                  {Object.entries(catalogueStats.catalogue || {}).map(([type, count]) => (
                    <Grid item xs={6} sm={4} md={3} key={type}>
                      <Card variant="outlined">
                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                          <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'capitalize' }}>
                            {type.replace(/([A-Z])/g, ' $1').trim()}
                          </Typography>
                          <Typography variant="h5">{count.toLocaleString()}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>

            {/* Barcode cache stats */}
            {catalogueStats.barcodeCache && (
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Barcode Cache</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Total cached lookups: {(catalogueStats.barcodeCache.total || 0).toLocaleString()}
                    &nbsp;·&nbsp; Books (isbn13): {(catalogueStats.barcodeCache.localBooks || 0).toLocaleString()}
                  </Typography>
                  {(catalogueStats.barcodeCache.byProvider || []).length > 0 && (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Provider</TableCell>
                            <TableCell align="right">Found</TableCell>
                            <TableCell align="right">Total</TableCell>
                            <TableCell align="right">Hit Rate</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {catalogueStats.barcodeCache.byProvider.map((row) => {
                            const pct = row.total > 0 ? Math.round((row.found / row.total) * 100) : 0;
                            return (
                              <TableRow key={row.provider}>
                                <TableCell sx={{ textTransform: 'capitalize' }}>{row.provider}</TableCell>
                                <TableCell align="right">{row.found.toLocaleString()}</TableCell>
                                <TableCell align="right">{row.total.toLocaleString()}</TableCell>
                                <TableCell align="right">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                                    <LinearProgress
                                      variant="determinate"
                                      value={pct}
                                      sx={{ width: 60, height: 6, borderRadius: 3 }}
                                      color={pct >= 80 ? 'success' : pct >= 40 ? 'warning' : 'error'}
                                    />
                                    <Typography variant="caption">{pct}%</Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {/* Per-type enrichment progress */}
                  {[
                    { label: 'Music (MusicBrainz)', total: catalogueStats.barcodeCache.localMusicTotal, checked: catalogueStats.barcodeCache.localMusicBarcodeChecked, withBarcode: catalogueStats.barcodeCache.localMusicWithBarcode },
                    { label: 'LEGO (Brickset)', total: catalogueStats.barcodeCache.localLegoTotal, checked: catalogueStats.barcodeCache.localLegoBarcodeChecked, withBarcode: catalogueStats.barcodeCache.localLegoWithBarcode },
                  ].filter(e => e.total > 0).map(e => (
                    <Box key={e.label} sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>{e.label}</Typography>
                      <Typography variant="body2">
                        Checked: {(e.checked || 0).toLocaleString()} / {e.total.toLocaleString()}
                        &nbsp;·&nbsp; Found barcodes: {(e.withBarcode || 0).toLocaleString()}
                        &nbsp;·&nbsp; Pending: {(e.total - (e.checked || 0)).toLocaleString()}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={e.total > 0 ? Math.round(((e.checked || 0) / e.total) * 100) : 0}
                        sx={{ mt: 1, height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  ))}
                </Paper>
              </Grid>
            )}

            {/* Quota tracker */}
            <Grid item xs={12} md={catalogueStats.barcodeCache ? 6 : 12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>API Quota Usage</Typography>
                {(catalogueStats.quotaTrackers || []).length === 0 ? (
                  <Typography variant="body2" color="textSecondary">No quota data available.</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {catalogueStats.quotaTrackers.map((q) => {
                      const limit = q.adminOverride?.limit ?? q.requestsLimit ?? 1000;
                      const used = q.requestsUsed ?? 0;
                      const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
                      return (
                        <Box key={q.apiName}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{q.apiName}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={q.enabled ? 'Enabled' : 'Disabled'}
                                size="small"
                                color={q.enabled ? 'success' : 'default'}
                                sx={{ height: 18, fontSize: '0.7rem' }}
                              />
                              <Typography variant="caption" color="textSecondary">
                                {used.toLocaleString()} / {limit.toLocaleString()} ({pct}%)
                              </Typography>
                            </Box>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            color={pct >= 90 ? 'error' : pct >= 70 ? 'warning' : 'primary'}
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      <Box sx={{ textAlign: 'center', color: 'text.secondary', mt: 2 }}>
        <Typography variant="caption">
          Statistics last updated: {formatDate(new Date())} at {formatTime(new Date())}
        </Typography>
      </Box>
    </Box>
  );
};

export default StatisticsDashboard;
